<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <v-row>
                <v-col cols="12" sm="12" md="8">
                    <h1>Transaction</h1>
                </v-col>
                <v-col cols="12" sm="12" md="4">
                    <v-text-field
                        v-model="data.quickSearchVal"
                        :label="CommonLabelTextHelper.getLabelText.filterCurrentPage"
                        @keydown="onKeydownQuickSearch"
                    ></v-text-field>
                </v-col>
            </v-row>
            <v-expansion-panels v-model="data.panel" multiple>
                <v-expansion-panel>
                    <v-expansion-panel-title>
                        <h2>Select date range</h2>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <date-range-select
                            v-model="data.searchFilterDateRangeGrp.dateTransactionOn"
                            :from-label="formLabelText.dateTransactionOnFrom"
                            :to-label="formLabelText.dateTransactionOnTo"
                            :enable-validation="true"
                            @validation-change="onDateRangeValidationChange"
                        />
                        <v-row class="mt-2">
                            <v-btn class="mr-2" variant="text" @click="onResetClick">
                                <v-icon>mdi-cached</v-icon>
                                Clear
                            </v-btn>
                            <v-spacer></v-spacer>
                            <v-btn class="mr-2" variant="text" @click="onSearchClick">
                                <v-icon>mdi-magnify</v-icon>
                                Search
                            </v-btn>
                        </v-row>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>
            <alert-message ref="errorAlert" :message="data.errorAlertMessage"></alert-message>
            <alert-message ref="paginationErrorAlert" :message="errorMessage"></alert-message>

            <!-- Delete Confirmation Dialog -->
            <v-dialog v-model="data.deleteOperation.showConfirmDialog" max-width="500">
                <v-card>
                    <v-card-title class="headline">
                        <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
                        Confirm Delete
                    </v-card-title>
                    <v-card-text>
                        Are you sure you want to delete this transaction record?
                        <br /><br />
                        <strong>Transaction ID:</strong>
                        {{ data.deleteOperation.selectedTransaction?.transactionId || '' }}
                        <br />
                        <strong>Order Number:</strong>
                        {{ data.deleteOperation.selectedTransaction?.orderNumber || '' }}
                        <br />
                        <strong>Source:</strong>
                        {{ data.deleteOperation.selectedTransaction?.transactionSource || '' }} <br /><br />
                        <v-alert type="warning" variant="tonal"> This action cannot be undone. </v-alert>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text @click="onCancelDelete" :disabled="data.deleteOperation.isLoading"> Cancel </v-btn>
                        <v-btn
                            color="error"
                            @click="onConfirmDelete"
                            :loading="data.deleteOperation.isLoading"
                            :disabled="data.deleteOperation.isLoading"
                        >
                            Delete
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <v-card>
                <v-card-title>
                    <div class="d-flex flex-wrap align-center ga-2 w-100">
                        <v-btn variant="text" @click="onGenerateClick"> Generate Transaction </v-btn>
                        <v-btn variant="text" @click="onSearchClick">
                            <v-icon>mdi-cached</v-icon>
                            Refresh
                        </v-btn>
                        <v-spacer></v-spacer>
                        <export-to-csv
                            :data="currentPageData"
                            :csv-header="data.csvExportConfig.csvHeader"
                            :filename="data.csvExportConfig.filename"
                            :formatter="csvFormatter"
                        ></export-to-csv>
                    </div>
                </v-card-title>

                <v-card-text>
                    <!-- Generic Table Component -->
                    <GenericListTable
                        :pagination="pagination"
                        :current-page-data="currentPageData"
                        :headers="headers"
                        :primary-key="'transactionId'"
                        :action-header-value="actionHeaderItem.value"
                        :quick-search-value="data.quickSearchVal"
                        :loading-message="'Loading more transaction data...'"
                        :is-loading="data.deleteOperation.isLoading"
                        @page-size-change="onPageSizeChange"
                        @page-select="onPageSelect"
                        @next-page="onNextPage"
                        @previous-page="onPreviousPage"
                    >
                        <template #table-actions="{ item, isLoading }">
                            <v-btn
                                :disabled="isLoading"
                                :loading="isLoading"
                                variant="text"
                                icon="mdi-delete"
                                color="error"
                                @click="onDeleteTransactionClick(item)"
                            >
                            </v-btn>
                        </template>
                    </GenericListTable>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import * as _ from 'lodash';
import { fetchWrapper } from '../../utils/FetchWrapper';
import { TransactionLabelTextHelper } from '../../utils/labelTextHelper/TransactionLabelTextHelper';
import { CommonLabelTextHelper } from '../../utils/labelTextHelper/CommonLabelTextHelper';
import { Formatter } from '../../utils/Formatter';
import { PathDefinition } from '../../utils/PathDefinition';
import DateRangeSelect from '../../components/DateRangeSelect.vue';
import AlertMessage from '../../components/AlertMessage.vue';
import ExportToCsv from '../../components/ExportToCsv.vue';
import GenericListTable from '../../components/GenericListTable.vue';
import { TransactionHeaderHelper } from '../../utils/headerHelper/TransactionHeaderHelper';
import { CsvFormatHelper } from '../../utils/CsvFormatHelper';
import { usePagination } from '../../composables/usePagination';
import { useUrlFilters } from '../../composables/useUrlFilters';
import { TransactionFilterDefinitions } from '../../utils/filterDefinitions/TransactionFilterDefinitions';

export default defineComponent({
    name: 'ListTransaction',

    components: {
        ExportToCsv,
        AlertMessage,
        GenericListTable,
        DateRangeSelect
    },

    setup() {
        const route = useRoute();

        const data = reactive({
            panel: [0],
            quickSearchVal: '',
            errorAlertMessage: '',
            searchFilterDateRangeGrpDefault: {},
            searchFilterDateRangeGrp: {
                dateTransactionOn: {
                    from: null,
                    to: null
                }
            },
            csvExportConfig: {
                filename: 'exportTransaction.csv',
                csvHeader: [...TransactionHeaderHelper.getCsvHeader()]
            },
            deleteOperation: {
                isLoading: false,
                showConfirmDialog: false,
                selectedTransaction: null
            },
            validation: {
                hasErrors: false
            }
        });

        // Initialize URL filter sync
        const { syncFiltersToUrl, restoreFiltersFromUrl, watchRouteChanges } = useUrlFilters({
            filterFields: TransactionFilterDefinitions,
            onFiltersRestored: (filters) => {
                // Apply restored filters to reactive state
                Object.keys(filters).forEach((key) => {
                    if (key.startsWith('date') && (key.endsWith('From') || key.endsWith('To'))) {
                        const dateField = key.replace(/From|To$/, '');
                        const direction = key.endsWith('From') ? 'from' : 'to';
                        data.searchFilterDateRangeGrp[dateField][direction] = filters[key];
                    }
                });
            }
        });

        // Initialize pagination
        const {
            pagination,
            currentPageData,
            errorMessage,
            loadPage,
            resetPagination,
            nextPage,
            previousPage,
            selectPage,
            changePageSize,
            removeItemFromAllPages,
            updateItemInAllPages,
            initialize
        } = usePagination({
            apiEndpoint: '/api/staff/transaction/list',
            buildApiParams: (page: number) => {
                const params: any = {};

                // Add date range filtering
                if (data.searchFilterDateRangeGrp.dateTransactionOn.from) {
                    params.dateTransactionOnFrom = Formatter.convertDateFromInputToUtcDateStr(
                        data.searchFilterDateRangeGrp.dateTransactionOn.from
                    );
                }

                if (data.searchFilterDateRangeGrp.dateTransactionOn.to) {
                    params.dateTransactionOnTo = Formatter.convertDateToInputToUtcDateStr(
                        data.searchFilterDateRangeGrp.dateTransactionOn.to
                    );
                }

                return params;
            }
        });

        onMounted(async () => {
            // Clone defaults BEFORE restoring from URL
            data.searchFilterDateRangeGrpDefault = _.cloneDeep(data.searchFilterDateRangeGrp);

            // Restore filters from URL (if any)
            const hasUrlFilters = Object.keys(route.query).length > 0;
            if (hasUrlFilters) {
                restoreFiltersFromUrl();
            }

            // Initialize pagination (single API call with restored filters)
            await initialize();

            // Watch for future route changes (back/forward buttons)
            watchRouteChanges();
        });

        return {
            datepickerPreviewFormat: Formatter.defaultDatepickerPreviewFormat,
            formLabelText: TransactionLabelTextHelper.getLabelText,
            CommonLabelTextHelper,
            actionHeaderItem: TransactionHeaderHelper.getActionHeaderItem(),
            headers: [...TransactionHeaderHelper.getHeaders()],
            data,
            // Pagination
            pagination,
            currentPageData,
            errorMessage,
            loadPage,
            resetPagination,
            nextPage,
            previousPage,
            selectPage,
            changePageSize,
            removeItemFromAllPages,
            updateItemInAllPages,
            // URL Filters
            syncFiltersToUrl
        };
    },
    computed: {},
    methods: {
        onKeydownQuickSearch(evt: any) {
            if (evt.key === 'Enter') {
                // Quick search is now handled by GenericListTable component
            }
        },
        async onResetClick() {
            this.data.searchFilterDateRangeGrp = _.cloneDeep(this.data.searchFilterDateRangeGrpDefault);

            // Clear URL query params
            await this.syncFiltersToUrl({});

            // Reload with defaults
            this.resetPagination();
            await this.loadPage(1, true);
        },
        async onSearchClick() {
            if (this.data.validation.hasErrors) {
                this.data.errorAlertMessage = 'Please fix the date range errors before searching';
                this.$refs.errorAlert?.showAlert();
                return;
            }

            // Flatten filters for URL (handle nested date ranges)
            const currentFilters = {
                dateTransactionOnFrom: this.data.searchFilterDateRangeGrp.dateTransactionOn.from,
                dateTransactionOnTo: this.data.searchFilterDateRangeGrp.dateTransactionOn.to
            };

            // Sync to URL BEFORE API call
            await this.syncFiltersToUrl(currentFilters);

            // Reset pagination and search with new filters
            this.resetPagination();
            await this.loadPage(1, true);
        },
        onDateRangeValidationChange(validationStatus: { hasErrors: boolean }) {
            this.data.validation.hasErrors = validationStatus.hasErrors;
        },
        async onGenerateClick() {
            await this.$router.push({ path: PathDefinition.generateTransaction });
        },
        csvFormatter: function (value: unknown, key: string, row: Record<string, unknown>): unknown {
            return CsvFormatHelper.formatDateTimeCsvValue(value as string, key);
        },
        onPageSizeChange() {
            this.changePageSize();
        },
        onPageSelect(page: number) {
            this.selectPage(page);
        },
        onNextPage() {
            this.nextPage();
        },
        onPreviousPage() {
            this.previousPage();
        },

        // Utility method for handling errors consistently
        handleError(error: any, message: string, showAlert: boolean = true) {
            console.error(message, error);
            if (showAlert) {
                this.data.errorAlertMessage = typeof error === 'string' ? error : message;
                this.$refs.errorAlert.showAlert();
            }
        },

        // Utility method for handling API responses
        handleApiResponse(response: any, successCallback: () => void, errorMessage?: string) {
            if (response.success) {
                successCallback();
            } else {
                const message = errorMessage || response.errors || 'Operation failed';
                this.handleError(response.errors, message);
            }
        },

        onDeleteTransactionClick(item: any) {
            this.data.deleteOperation.selectedTransaction = item;
            this.data.deleteOperation.showConfirmDialog = true;
        },

        onCancelDelete() {
            this.data.deleteOperation.showConfirmDialog = false;
            this.data.deleteOperation.selectedTransaction = null;
        },

        async onConfirmDelete() {
            if (!this.data.deleteOperation.selectedTransaction) {
                return;
            }

            this.data.deleteOperation.isLoading = true;

            try {
                const transactionId = this.data.deleteOperation.selectedTransaction.transactionId;
                const requestUrl = `/api/staff/transaction/batchDelete`;
                const requestData = {
                    transactionIdList: [transactionId]
                };

                const response = await fetchWrapper.post(requestUrl, requestData);

                this.handleApiResponse(
                    response,
                    () => {
                        // Remove the item using the pagination composable
                        this.removeItemFromAllPages(transactionId, 'transactionId');

                        // Close dialog and reset state
                        this.data.deleteOperation.showConfirmDialog = false;
                        this.data.deleteOperation.selectedTransaction = null;
                    },
                    'Failed to delete transaction'
                );
            } catch (error) {
                this.handleError(error, 'An unexpected error occurred while deleting the transaction');
            } finally {
                this.data.deleteOperation.isLoading = false;
            }
        }
    }
});
</script>

<style>
.validation-error {
    color: red;
}
</style>
